/**
 * « Ce que les professeurs ont demandé » — en tête du travail du soir.
 *
 * Demandé par Hedi le 8 septembre 2026 : *« dans mon travail, rajoute aussi au
 * tout au début le travail demandé via le cahier de texte. À titre informatif
 * pour centraliser l'information que voit l'enfant. C'est là que le bouton pour
 * prendre en photo des exercices doit être mis pour comprendre ce sur quoi
 * portent les exercices. »*
 *
 * **La seule carte de la coquille enfant qui ne conclut rien.** Tout le reste de
 * cet écran est une proposition du système ; ceci est la phrase du professeur,
 * recopiée. C'est aussi la seule chose qui reste quand le rattachement échoue —
 * le 6 septembre 2026, douze devoirs sur douze étaient non rattachés, et l'enfant
 * n'avait alors nulle part où lire ce qu'on lui demandait.
 *
 * **La photo, elle, décide.** « Exercices 12 à 18 p. 45 » ne nomme aucun
 * chapitre : ni le rattachement lexical ni le modèle ne peuvent en tirer quoi
 * que ce soit. La page photographiée, si. Elle entre dans la lecture de la
 * journée (`core_api.internal.journal_lecture`) et sert à dire *sur quoi porte*
 * l'exercice — jamais qu'il a été traité en cours, et jamais dans `mastery` :
 * une photo n'est pas une observation vérifiée (règle 3).
 *
 * **Ce n'est pas le bouton de `RenduAuParent`**, qui se ressemble et fait
 * l'inverse : là-bas l'enfant photographie *sa réponse* à un exercice que nous
 * avons produit et que nous connaissons entièrement, et un parent la corrige.
 * Ici il photographie *l'énoncé* d'un exercice que nous ne connaissons pas.
 *
 * La photo part directement au stockage par URL présignée ; seule sa clé
 * traverse l'API (invariant 7), et les vignettes sont servies sous URL signée à
 * durée de vie courte (règle 8).
 *
 * **L'invitation ne dure que deux jours** — Hedi, le 10 septembre 2026 :
 * *« uniquement pendant 2 jours. Si pas d'images envoyée, n'affiche plus. »* Une
 * demande que l'on décline tous les soirs est une demande que l'on cesse de
 * lire. C'est le serveur qui tranche (`photo_proposee`), pour la même raison que
 * l'heure d'ouverture du journal du soir : l'horloge d'une tablette se règle à
 * la main. L'écran n'en refait pas le calcul, il lit la réponse.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import type { DevoirDuCahier, DevoirsDuCahier as Reponse } from "@/api/schema";
import { uploadPhoto } from "@/photo/upload";
import { enClairCourt } from "@/ui/dates";
import { contenuLisible } from "@/ui/imagesDuCahier";
import { etiquette } from "@/ui/matieres";
import { Button, Card, ErrorNote } from "@/ui/primitives";

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne" }
  | { quoi: "pret"; reponse: Reponse };

/**
 * « Rien » a deux causes opposées, et elles n'appellent pas le même geste.
 *
 * Aucun devoir avec un import récent, c'est une bonne nouvelle. Aucun devoir
 * sans aucun import, c'est le connecteur qui n'a jamais tourné — et le dire
 * évite à l'enfant de croire qu'il n'a rien à faire.
 */
export function rienADire(derniere: string | null | undefined): string {
  return derniere
    ? "Les professeurs n'ont rien demandé pour les prochains jours."
    : "Je n'ai pas encore réussi à lire le cahier de textes.";
}

export function DevoirsDuCahier() {
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  const charger = useCallback(async () => {
    try {
      setEtat({ quoi: "pret", reponse: await call("mesDevoirs") });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Une carte informative ne doit pas peser sur l'écran qui sert les exercices :
  // tant qu'elle charge, elle n'occupe rien, et si elle tombe elle le dit sans
  // empêcher le travail du soir.
  if (etat.quoi === "chargement") return null;

  if (etat.quoi === "panne") {
    return (
      <Card title="Ce que les professeurs ont demandé">
        <ErrorNote>Je n'arrive pas à lire le cahier de textes.</ErrorNote>
        <Button variant="quiet" onClick={() => void charger()}>
          Réessayer
        </Button>
      </Card>
    );
  }

  const devoirs = etat.reponse.devoirs ?? [];

  return (
    <Card title="Ce que les professeurs ont demandé">
      {devoirs.length === 0 ? (
        <p className="muted small">{rienADire(etat.reponse.derniere_synchro)}</p>
      ) : (
        <ul className="devoirs__liste">
          {devoirs.map((devoir) => (
            <Devoir key={devoir.source_id} devoir={devoir} rafraichir={charger} />
          ))}
        </ul>
      )}
    </Card>
  );
}

type Envoi = "repos" | "envoi" | "panne";

function Devoir({ devoir, rafraichir }: { devoir: DevoirDuCahier; rafraichir: () => Promise<void> }) {
  const [envoi, setEnvoi] = useState<Envoi>("repos");
  const photos = devoir.photos ?? [];
  // Le champ est facultatif au contrat : absent, on propose. Un serveur d'avant
  // cette version retirerait sinon le bouton en silence, et l'enfant n'aurait
  // aucun moyen de savoir que la photo existe.
  const propose = devoir.photo_proposee ?? true;

  const photographier = async (file: File) => {
    setEnvoi("envoi");
    try {
      const photo = await uploadPhoto(file);
      await call("photographierUnDevoir", {
        body: { source_id: devoir.source_id, key: photo.key },
      });
      // La route a rouvert la lecture de la journée : on relit la liste plutôt
      // que d'ajouter la vignette de notre côté, l'URL signée venant du serveur.
      await rafraichir();
      setEnvoi("repos");
    } catch {
      setEnvoi("panne");
    }
  };

  const retirer = async (photoId: string) => {
    try {
      await call("retirerUnePhoto", { params: { photo_id: photoId } });
      await rafraichir();
    } catch {
      setEnvoi("panne");
    }
  };

  return (
    <li className="devoirs__ligne">
      <p className="devoirs__entete">
        <strong>{etiquette(devoir.matiere)}</strong>
        {devoir.pour_le ? (
          <span className="muted small"> · pour le {enClairCourt(devoir.pour_le)}</span>
        ) : null}
      </p>
      <p className="devoirs__texte">{contenuLisible(devoir.contenu)}</p>

      {photos.length > 0 ? (
        <ul className="devoirs__photos">
          {photos.map((photo, index) => (
            <li key={photo.id}>
              <a href={photo.url} target="_blank" rel="noreferrer">
                <img
                  className="devoirs__photo"
                  src={photo.url}
                  alt={`Photo ${index + 1} de l'énoncé`}
                  loading="lazy"
                />
              </a>
              <Button variant="quiet" onClick={() => void retirer(photo.id)}>
                Retirer
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {propose ? (
        <label className="devoirs__prise">
          <span className="muted small">
            {photos.length === 0
              ? "Photographie l'énoncé pour que je sache sur quoi il porte."
              : "Ajouter une autre page."}
          </span>
          <input
            className="input"
            type="file"
            accept="image/*"
            capture="environment"
            disabled={envoi === "envoi"}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void photographier(file);
            }}
          />
        </label>
      ) : null}
      {envoi === "envoi" ? <p className="muted small">Envoi de la photo…</p> : null}
      {envoi === "panne" ? <ErrorNote>La photo n'est pas partie. Réessaie.</ErrorNote> : null}
    </li>
  );
}
